"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, BookOpen, Calculator, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const RELATED_LINKS = [{
  title: "Remodeling Cost Guide",
  description: "Explore budget benchmarks, scope estimates, and historic home investment factors across Decatur.",
  to: "/remodeling-cost-guide",
  icon: Calculator
}, {
  title: "Remodeling Planning Guide",
  description: "Step-by-step roadmap from initial architectural design and zoning permits through project completion.",
  to: "/remodeling-planning-guide",
  icon: BookOpen
}, {
  title: "Contact",
  description: "Speak directly with our team, review active project timelines, or schedule an in-home walkthrough.",
  to: "/ContactPage",
  icon: MessageSquare
}];
const PROJECT_TYPES = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const CONTACT_METHODS = ["Phone", "Text", "Email"];
const TIMING_OPTIONS = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function EstimateForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [pagePath, setPagePath] = useState("/");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setPagePath(window.location.pathname || "/");
    }
  }, []);
  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent to proceed.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", pagePath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimateformandrelatedlinks" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Related Links Block */}
        <div className="mb-14 sm:mb-20">
          <div className="max-w-2xl mb-8 sm:mb-10">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground">
              Helpful Guides & Resources
            </h2>
            <p className="mt-2 text-muted-foreground text-sm sm:text-base">
              Plan your renovation with our cost guides, design playbooks, and direct project support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RELATED_LINKS.map((link, i) => {
            const IconComponent = link.icon;
            return <div key={link.to} data-index={i}>
                  <Card className="h-full border border-border/80 bg-card hover:border-foreground/30 transition-all duration-200 shadow-sm hover:shadow-md rounded-xl flex flex-col justify-between">
                    <CardHeader className="pb-4">
                      <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                        <IconComponent className="size-5 text-foreground" />
                      </div>
                      <CardTitle className="font-serif text-xl text-card-foreground">
                        {link.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-1.5">
                        {link.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Button asChild variant="ghost" size="sm" className="px-0 text-foreground hover:bg-transparent hover:underline inline-flex items-center gap-1.5 font-medium">
                        <Link to={link.to}>
                          Read guide
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {/* Universal Estimate Form Section */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border bg-card/95 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="border-b border-border/60 bg-muted/20 px-6 sm:px-10 py-8">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                <Sparkles className="size-3.5 text-foreground" />
                <span>Complimentary Project Consultation</span>
              </div>
              <CardTitle className="font-serif text-2xl sm:text-3xl text-card-foreground font-normal">
                Request an Estimate
              </CardTitle>
              <CardDescription className="text-muted-foreground text-sm sm:text-base mt-1">
                Share a few details regarding your upcoming project. Our team will review your scope and follow up promptly.
              </CardDescription>
            </CardHeader>

            <CardContent className="px-6 sm:px-10 py-8">
              {isSuccess ? <div className="py-12 px-4 text-center space-y-4">
                  <div className="size-14 mx-auto rounded-full bg-secondary flex items-center justify-center">
                    <CheckCircle2 className="size-8 text-foreground" />
                  </div>
                  <h3 className="font-serif text-2xl text-foreground">
                    Estimate Request Received
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base leading-relaxed">
                    Thank you for reaching out. We have received your project details and will be in touch shortly through your preferred contact method.
                  </p>
                  <div className="pt-4">
                    <Button variant="outline" onClick={() => {
                  setIsSuccess(false);
                  setFormData({
                    name: "",
                    phone: "",
                    email: "",
                    address: "",
                    project_type: "",
                    details: "",
                    contact_method: "",
                    timing: "",
                    budget: "",
                    consent: false
                  });
                }}>
                      Submit Another Request
                    </Button>
                  </div>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {errorMessage && <Alert variant="destructive" className="border-destructive/40">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Notice</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-name">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-name" type="text" required placeholder="Sarah Jenkins" value={formData.name} onChange={e => handleChange("name", e.target.value)} disabled={isSubmitting} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-phone">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} disabled={isSubmitting} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-email">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => handleChange("email", e.target.value)} disabled={isSubmitting} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-address">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-address" type="text" required placeholder="e.g. 30030 or Ponce de Leon Ave" value={formData.address} onChange={e => handleChange("address", e.target.value)} disabled={isSubmitting} />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-project-type">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => handleChange("project_type", val)} disabled={isSubmitting}>
                        <SelectTrigger id="estimate-project-type" className="w-full">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROJECT_TYPES.map(type => <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-contact-method">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => handleChange("contact_method", val)} disabled={isSubmitting}>
                        <SelectTrigger id="estimate-contact-method" className="w-full">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_METHODS.map(method => <SelectItem key={method} value={method}>
                              {method}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-timing">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => handleChange("timing", val)} disabled={isSubmitting}>
                        <SelectTrigger id="estimate-timing" className="w-full">
                          <SelectValue placeholder="Select timeline" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMING_OPTIONS.map(time => <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-budget">Estimated Budget</Label>
                      <Select value={formData.budget} onValueChange={val => handleChange("budget", val)} disabled={isSubmitting}>
                        <SelectTrigger id="estimate-budget" className="w-full">
                          <SelectValue placeholder="Select estimated budget" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map(budget => <SelectItem key={budget} value={budget}>
                              {budget}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-details">Project Details</Label>
                    <Textarea id="estimate-details" rows={4} placeholder="Tell us about your home, current layout, specific goals, or any design ideas you have in mind..." value={formData.details} onChange={e => handleChange("details", e.target.value)} disabled={isSubmitting} />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="pt-2">
                    <div className="flex items-start gap-3 rounded-lg border border-border/70 p-4 bg-muted/20">
                      <Checkbox id="estimate-consent" checked={formData.consent} onCheckedChange={checked => handleChange("consent", Boolean(checked))} disabled={isSubmitting} className="mt-0.5" />
                      <div className="grid gap-1.5 leading-none">
                        <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-foreground leading-snug cursor-pointer">
                          I agree to be contacted about my project by phone, text, or email.
                        </Label>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full h-12 text-base font-medium shadow-sm transition-all">
                    {isSubmitting ? <>
                        <Loader2 className="size-5 animate-spin" />
                        <span>Sending Request...</span>
                      </> : <span>Request Free Estimate</span>}
                  </Button>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}